import { z } from 'zod';
import { CONTACT_CATEGORIES } from './contacts.js';
/**
 * Contact Types manager (V34 Settings → Contact Types). A richer view of the
 * `contact-types` lookup: an ordered list of types (each active/system/position/
 * pipeline-linked) plus a per-type custom-field template. The flat
 * `/lookups/contact-types` endpoint still serves name-only reads elsewhere.
 */
/** Pipeline categories a Contact Type can link to (labels for the picker). */
export const CONTACT_TYPE_CATEGORY_LABELS = {
    client: 'Clients',
    referral_source: 'Referral Sources',
    staff: 'Staff',
};
/**
 * Field kinds a Contact Type field can be. Free-form vs. the form-builder
 * `FieldType` enum — the manager's set is richer (currency, email, phone, url,
 * checkboxes, leadsourcetype, state) and independent of form rendering.
 */
export const CONTACT_FIELD_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Long text' },
    { value: 'number', label: 'Number' },
    { value: 'currency', label: 'Currency' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Dropdown' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'checkboxes', label: 'Checkboxes (multiple)' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'url', label: 'URL' },
    { value: 'leadsourcetype', label: 'Lead/Referral Source' },
];
/** All accepted field-type values (the picker set + `state`, used by seeds). */
export const CONTACT_FIELD_TYPE_VALUES = [
    'text',
    'textarea',
    'number',
    'currency',
    'date',
    'select',
    'checkbox',
    'checkboxes',
    'email',
    'phone',
    'url',
    'leadsourcetype',
    'state',
];
/** The four seeded sections a field can live in (plus the "Other Fields" catch-all). */
export const CONTACT_TYPE_SECTIONS = [
    'Personal Information',
    'Contact Information',
    'Address Information',
    'Company Information',
];
// ── Contact Type CRUD ────────────────────────────────────────────────────────
export const contactTypeCreateSchema = z.object({
    name: z.string().min(1).max(120),
});
/** Patch: rename (custom only), toggle active, (re)link category, set position. */
export const contactTypeUpdateSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    active: z.boolean().optional(),
    // '' / null clears the pipeline link.
    category: z.enum(CONTACT_CATEGORIES).nullable().optional(),
    position: z.number().int().min(0).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
/** Bulk reorder — the full ordered list of type ids after a drag. */
export const contactTypeReorderSchema = z.object({
    ids: z.array(z.string().uuid()).min(1),
});
// ── Field template CRUD ──────────────────────────────────────────────────────
export const contactTypeFieldCreateSchema = z.object({
    label: z.string().min(1).max(120),
    fieldType: z.enum(CONTACT_FIELD_TYPE_VALUES).default('text'),
    section: z.string().min(1).max(80).default('Other Fields'),
    options: z.array(z.string().max(200)).max(100).optional(),
});
export const contactTypeFieldUpdateSchema = z
    .object({
    label: z.string().min(1).max(120).optional(),
    fieldType: z.enum(CONTACT_FIELD_TYPE_VALUES).optional(),
    section: z.string().min(1).max(80).optional(),
    options: z.array(z.string().max(200)).max(100).optional(),
    position: z.number().int().min(0).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
export const contactTypeFieldReorderSchema = z.object({
    ids: z.array(z.string().uuid()).min(1),
});
//# sourceMappingURL=contact-types.js.map